import { useQuery } from '@tanstack/react-query';

import { QueryPagination, createQueryOptions } from '../../../query';
import { useStoreApi } from '../../api';
import { storeKeys } from '../../query-key-factory';
import type { StoreItemResponse } from '../../types';
import { TicketSearch } from './search.types';
import { TicketStoreItemType, TicketsResponse, TicketsType } from './types';

const useListTickets = <TTicketType extends TicketsType = 'tickets'>({
  eventId,
  causeId,
  pagination,
  queryOptions,
  enabled = true,
  ticketsType = 'tickets' as TTicketType,
}: {
  /**
   * the event id (aka campaign id)
   */
  eventId?: string;
  causeId?: string;
  pagination?: QueryPagination;
  queryOptions?: TicketSearch;
  enabled?: boolean;
  ticketsType?: TTicketType;
}) => {
  const StoreApi = useStoreApi();
  const queryParams = new URLSearchParams();

  if (causeId) {
    queryParams.append('cause_id', causeId);
  }

  const getTickets = async (context: { meta?: { queryString?: string } }) => {
    const { queryString } = context.meta || {};

    const { data } = await StoreApi.get<TicketsResponse<TTicketType>>(
      `/store_items/events/${eventId}/${ticketsType}?${queryString}`
    );

    return data;
  };

  const { queryString, queryObject, queryPagination } = createQueryOptions<TicketSearch>({
    search: queryOptions,
    pagination,
    existingSearchString: queryParams.toString(),
  });

  const ticketsKey = ticketsType === 'tickets' ? 'tickets' : 'seasonPasses';
  const key =
    !!causeId && !!eventId ? storeKeys.id(causeId).items.events.id(eventId)[ticketsKey].all : [];

  return useQuery({
    queryFn: getTickets,
    queryKey: [...key, queryObject, queryPagination],
    meta: { queryString },
    enabled: !!causeId && !!eventId && enabled,
  });
};

const useGetTicket = <T extends TicketsType>({
  eventId,
  causeId,
  ticketId,
  ticketsType = 'tickets' as T,
}: {
  eventId?: string;
  causeId?: string;
  ticketId?: string;
  ticketsType?: T;
}) => {
  // create object, check how it's done on another branch to avoid conflicts
  const StoreApi = useStoreApi();

  const getTicket = async () => {
    const { data } = await StoreApi.get<StoreItemResponse<TicketStoreItemType<T>>>(
      `/store_items/events/${eventId}/${ticketsType}/${ticketId}?cause_id=${causeId}`
    );
    return data.store_item;
  };

  const ticketsKey = ticketsType === 'tickets' ? 'tickets' : 'seasonPasses';
  const key =
    causeId && eventId && ticketId
      ? storeKeys.id(causeId).items.events.id(eventId)[ticketsKey].id(ticketId).detail
      : [];

  return useQuery({
    queryKey: key,
    queryFn: getTicket,
    enabled: !!causeId && !!eventId && !!ticketId,
  });
};

export { useGetTicket, useListTickets };
